import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Input } from "@nextui-org/react";
import type { Role } from "@/types";
import { BackArrowIcon } from "@/assets/icons/admin";
import CustomChipDropdown from "@/components/admin/chipdropdown";
import {
  useCreateRole,
  useGetAllPrivileges,
  useGetAllRoles,
  useUpdateRole,
} from "@/services/admin.service";

interface EditPrivilegeViewProps {
  onCancel: () => void;
  itemData: Role | undefined;
}

const EditPrivilegeView: React.FC<EditPrivilegeViewProps> = (props) => {
  const { onCancel, itemData } = props;

  const { data: getAllPrivilegesData } = useGetAllPrivileges();
  const { refetch } = useGetAllRoles({ page: 1, pageSize: 10 });

  const updateRole = useUpdateRole();
  const { mutate: createRole } = useCreateRole();

  const authorizations = getAllPrivilegesData?.data?.map((item: Role) => ({
    label: item.name,
    value: item.id,
  }));

  const options: string[] = authorizations?.map(
    (account: { label: string }) => account.label,
  );

  const [groupName, setGroupName] = useState("");
  const [selectedPrivileges, setSelectedPrivileges] = useState<
    string[] | undefined
  >();

  useEffect(() => {
    if (itemData) {
      if (!itemData.privileges) return;

      const selectedPrivileges = itemData.privileges
        ?.map((selectedValue: number) => {
          const option = authorizations?.find(
            (option: { value: number }) => option.value === selectedValue,
          );
          return option ? option.label : null;
        })
        .filter((label) => label !== null);

      setSelectedPrivileges(selectedPrivileges);

      setGroupName(itemData.name);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(e.target.value);
  };

  function handleSubmit(): void {
    if (!groupName || !selectedPrivileges) {
      toast.error("Please fill in all fields");
      return;
    }

    const selectedValues = selectedPrivileges
      .map((selectedLabel) => {
        const option = authorizations.find(
          (option: { label: string }) => option.label === selectedLabel,
        );
        return option ? option.value : null;
      })
      .filter((value) => value !== null); // This will remove any null values if there were no matches

    const submitData: any = {
      name: groupName,
      privileges: selectedValues,
    };

    if (itemData === undefined) {
      createRole(submitData, {
        onSuccess: () => {
          refetch();
          toast.success("Role created successfully");
        },
        onError: () => {
          toast.error("Error creating Role");
        },
      });
    } else {
      updateRole.mutate(
        { id: itemData.id!, data: submitData },
        {
          onSuccess: () => {
            refetch();
            toast.success("Role updated successfully");
          },
          onError: (error: unknown) => {
            console.error("Error creating User", error);
            toast.error("Error updated Role");
          },
        },
      );
    }
  }

  return (
    <div>
      <div className="font-ubuntu w-full text-center text-2xl font-semibold text-white sm:float-left">
        <span>
          <Button
            isIconOnly
            onClick={onCancel}
            className="transparent-bg float-left ml-[-11px] h-auto w-auto"
          >
            <BackArrowIcon />
          </Button>
          <p className="text-sm sm:float-left sm:text-lg md:text-lg lg:text-lg">
            {itemData ? "Edit" : "Add"} Group Privilege
          </p>
        </span>
      </div>
      <div className="w-full">
        <div className="mt-4 flex w-full flex-col">
          <label
            htmlFor="firstName"
            className="field-label mb-2 mt-4 block text-sm font-normal text-white"
          >
            Group Name
          </label>
          <div></div>
          <Input
            value={groupName}
            onChange={handleInputChange}
            placeholder="Input Group Name"
            name="groupName"
            className="w-full !cursor-text"
            classNames={{
              inputWrapper: [
                "input-bg",
                `${itemData !== undefined && "data-added-input-bg"}`,
                "!cursor-text",
                "rounded-md",
                "border",
                "border-solid",
                "border-white",
                "border-opacity-50",
                "text-sm",
              ],
            }}
            isReadOnly={itemData !== undefined}
            required
          ></Input>
        </div>
        <div className="mt-3 flex w-full flex-col">
          <label
            htmlFor="firstName"
            className="field-label mb-2 block text-sm font-normal text-white"
          >
            Page Authorization
          </label>
          <CustomChipDropdown
            options={options}
            selectedPrivileges={selectedPrivileges}
            setSelectedPrivileges={setSelectedPrivileges}
          />
        </div>
      </div>
      <div className="mt-10 flex w-full flex-col gap-6">
        <div className="flex flex-row">
          <div className="mt-[6.5rem] w-full">
            <div className="sm:float-right">
              <Button
                onClick={handleSubmit}
                className="admin-common-button-gradient font-ubuntu z-0 w-full rounded-md pl-10 pr-10 text-base font-bold"
              >
                <span className="hidden sm:flex md:flex lg:flex xl:flex 2xl:flex">
                  Save
                </span>
                <span className="flex flex-col sm:hidden md:hidden lg:hidden xl:hidden 2xl:hidden">
                  Save Changes
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPrivilegeView;
